/**
 * CoachingSurface — Manager coaching triggers (W18, 2026-05-27)
 *
 * Four view-only lists for the manager:
 *   (a) Reps with zero forward movement this week
 *   (b) Accounts untouched 14+ days
 *   (c) Deals with no upward path lit (zero green M-PACT Checkpoints)
 *   (d) Reps below 5X pipeline coverage
 *
 * Each list has click-through to the relevant account or rep page.
 * Tenant 1 only. View-only — no mutations.
 *
 * Canon rules: no emoji, no exclamation, M-PACT hyphenated, "Checkpoint" not "Gate".
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/session";
import { tokens } from "@/components/operator/tokens";

// ─── Types ──────────────────────────────────────────────────────────────────

interface RepRow {
  id: string;
  full_name: string;
  quota_annual: number;
}

interface AccountRow {
  id: number;
  name: string;
  updated_at: string;
  owner_user_id: string;
}

interface CpStatusRow {
  account_id: number;
  status: string | null;
}

interface PipelineRow {
  ae_user_id: string;
  open_value: number | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function fmt(name: string): string {
  const parts = name.split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function CoachingSection({
  title,
  subtitle,
  children,
  count,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  count: number;
}) {
  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 8,
        background: tokens.color.canvas,
        overflow: "hidden",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "7px 12px",
          borderBottom: `1px solid ${tokens.color.border}`,
          background: tokens.color.surfaceMute,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: tokens.font.body,
              fontWeight: 700,
              fontSize: 12,
              color: tokens.color.textPrimary,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </div>
          <div style={{ fontFamily: tokens.font.body, fontSize: 11, color: tokens.color.textMuted, marginTop: 1 }}>
            {subtitle}
          </div>
        </div>
        <span
          style={{
            background: count > 0 ? "#FEE2E2" : "#F4F4F5",
            color: count > 0 ? "#9A1F1F" : tokens.color.textMuted,
            border: count > 0 ? "1px solid #FCA5A5" : `1px solid ${tokens.color.border}`,
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            fontFamily: tokens.font.body,
          }}
        >
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ padding: "8px 12px", fontSize: 12, color: tokens.color.textMuted, fontFamily: tokens.font.body }}>
      {message}
    </div>
  );
}

function ItemRow({
  primary,
  secondary,
  badge,
  onClick,
}: {
  primary: string;
  secondary?: string;
  badge?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        borderTop: `1px solid ${tokens.color.borderSoft}`,
        cursor: onClick ? "pointer" : "default",
        fontFamily: tokens.font.body,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: tokens.color.textPrimary }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 2 }}>{secondary}</div>
        )}
      </div>
      {badge && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: tokens.color.danger,
            background: "#FEE2E2",
            border: "1px solid #FCA5A5",
            borderRadius: 4,
            padding: "2px 7px",
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface Props {
  tenantId: number;
}

export default function CoachingSurface({ tenantId }: Props) {
  const [, setLocation] = useLocation();

  const [loading, setLoading] = useState(true);
  const [reps, setReps] = useState<RepRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [cpStatus, setCpStatus] = useState<CpStatusRow[]>([]);
  const [pipeline, setPipeline] = useState<PipelineRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();

        // AE reps only
        const { data: repData } = await sb
          .from("users")
          .select("id, full_name, quota_annual")
          .eq("tenant_id", tenantId)
          .eq("role", "ae");

        const repRows = (repData ?? []) as RepRow[];
        const repIds = repRows.map((r) => r.id);

        // All accounts owned by AEs in this tenant
        const { data: accData } = repIds.length
          ? await sb
              .from("accounts")
              .select("id, name, updated_at, owner_user_id")
              .eq("tenant_id", tenantId)
              .in("owner_user_id", repIds)
          : { data: [] };

        const accRows = (accData ?? []) as AccountRow[];
        const accIds = accRows.map((a) => a.id);

        // CP status for all accounts
        const { data: cpData } = accIds.length
          ? await sb
              .from("account_cp_status")
              .select("account_id, status")
              .in("account_id", accIds)
          : { data: [] };

        // Pipeline summary: open_value per rep
        // Note: v_ae_pipeline_summary uses integer tenant_id; cast avoids type mismatch
        const { data: pipeData } = repIds.length
          ? await sb
              .from("v_ae_pipeline_summary")
              .select("ae_user_id, open_value")
              .in("ae_user_id", repIds)
          : { data: [] };

        if (cancelled) return;
        setReps(repRows);
        setAccounts(accRows);
        setCpStatus((cpData ?? []) as CpStatusRow[]);
        setPipeline((pipeData ?? []) as PipelineRow[]);
      } catch {
        // Non-fatal — sections render as empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // ── (a) Reps with zero forward movement this week ─────────────────────────
  // Proxy: reps where none of their accounts had updated_at within last 7 days.
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const repsWithNoMovement: RepRow[] = reps.filter((rep) => {
    const repAccounts = accounts.filter((a) => a.owner_user_id === rep.id);
    if (repAccounts.length === 0) return true;
    return !repAccounts.some((a) => Date.now() - new Date(a.updated_at).getTime() < WEEK_MS);
  });

  // ── (b) Accounts untouched 14+ days ──────────────────────────────────────
  const untouchedAccounts: AccountRow[] = accounts.filter(
    (a) => daysSince(a.updated_at) >= 14,
  );

  // ── (c) Deals with no upward path lit (zero green M-PACT Checkpoints) ────
  const cpByAccount = new Map<number, CpStatusRow[]>();
  for (const c of cpStatus) {
    if (!cpByAccount.has(c.account_id)) cpByAccount.set(c.account_id, []);
    cpByAccount.get(c.account_id)!.push(c);
  }
  const noGreenPathAccounts: AccountRow[] = accounts.filter((a) => {
    const cps = cpByAccount.get(a.id) ?? [];
    if (cps.length === 0) return false;
    return cps.every((c) => c.status !== "green");
  });

  // ── (d) Reps below 5X pipeline coverage ──────────────────────────────────
  const pipeByRep = new Map<string, number>();
  for (const p of pipeline) {
    pipeByRep.set(p.ae_user_id, Number(p.open_value ?? 0));
  }
  const repsBelowCoverage: Array<{ rep: RepRow; coverage: number }> = reps
    .filter((r) => {
      const quota = Number(r.quota_annual ?? 0);
      if (quota === 0) return false;
      return (pipeByRep.get(r.id) ?? 0) / quota < 5;
    })
    .map((r) => {
      const quota = Number(r.quota_annual ?? 1);
      const pipe = pipeByRep.get(r.id) ?? 0;
      return { rep: r, coverage: Math.round((pipe / quota) * 10) / 10 };
    });

  const repById = new Map(reps.map((r) => [r.id, r]));

  if (loading) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: tokens.color.textMuted, fontFamily: tokens.font.body }}>
        Loading coaching data...
      </div>
    );
  }

  return (
    <div data-testid="coaching-surface">

      {/* (a) Zero forward movement */}
      <CoachingSection
        title="No forward movement this week"
        subtitle="Reps with no account updates in the last 7 days"
        count={repsWithNoMovement.length}
      >
        {repsWithNoMovement.length === 0 ? (
          <EmptyState message="All reps have accounts updated this week." />
        ) : (
          repsWithNoMovement.map((rep) => {
            const count = accounts.filter((a) => a.owner_user_id === rep.id).length;
            return (
              <ItemRow
                key={rep.id}
                primary={rep.full_name}
                secondary={`${count} account${count !== 1 ? "s" : ""} — no updates in 7 days`}
                badge="No movement"
                onClick={() => setLocation(`/accounts?owner=${rep.id}`)}
              />
            );
          })
        )}
      </CoachingSection>

      {/* (b) Accounts untouched 14+ days */}
      <CoachingSection
        title="Untouched 14+ days"
        subtitle="Accounts with no recorded update in two weeks"
        count={untouchedAccounts.length}
      >
        {untouchedAccounts.length === 0 ? (
          <EmptyState message="No accounts untouched for 14 or more days." />
        ) : (
          <>
            {untouchedAccounts.slice(0, 10).map((a) => {
              const owner = repById.get(a.owner_user_id);
              return (
                <ItemRow
                  key={a.id}
                  primary={a.name}
                  secondary={owner ? fmt(owner.full_name) : undefined}
                  badge={`${daysSince(a.updated_at)}d idle`}
                  onClick={() => setLocation(`/account/${a.id}`)}
                />
              );
            })}
            {untouchedAccounts.length > 10 && (
              <div style={{ padding: "5px 12px", fontSize: 11, color: tokens.color.textMuted, borderTop: `1px solid ${tokens.color.borderSoft}`, fontFamily: tokens.font.body }}>
                +{untouchedAccounts.length - 10} more
              </div>
            )}
          </>
        )}
      </CoachingSection>

      {/* (c) No green Checkpoints */}
      <CoachingSection
        title="No upward path lit"
        subtitle="Accounts with zero green Checkpoints"
        count={noGreenPathAccounts.length}
      >
        {noGreenPathAccounts.length === 0 ? (
          <EmptyState message="All scored accounts have at least one green Checkpoint." />
        ) : (
          <>
            {noGreenPathAccounts.slice(0, 10).map((a) => {
              const owner = repById.get(a.owner_user_id);
              const cpCount = (cpByAccount.get(a.id) ?? []).length;
              return (
                <ItemRow
                  key={a.id}
                  primary={a.name}
                  secondary={`${cpCount} Checkpoint${cpCount !== 1 ? "s" : ""} — none green${owner ? ` · ${fmt(owner.full_name)}` : ""}`}
                  badge="No green CP"
                  onClick={() => setLocation(`/account/${a.id}`)}
                />
              );
            })}
            {noGreenPathAccounts.length > 10 && (
              <div style={{ padding: "5px 12px", fontSize: 11, color: tokens.color.textMuted, borderTop: `1px solid ${tokens.color.borderSoft}`, fontFamily: tokens.font.body }}>
                +{noGreenPathAccounts.length - 10} more
              </div>
            )}
          </>
        )}
      </CoachingSection>

      {/* (d) Below 5X coverage */}
      <CoachingSection
        title="Below 5X pipeline coverage"
        subtitle="Reps with pipeline open value less than 5x annual quota"
        count={repsBelowCoverage.length}
      >
        {repsBelowCoverage.length === 0 ? (
          <EmptyState message="All reps are at or above 5X pipeline coverage." />
        ) : (
          repsBelowCoverage.map(({ rep, coverage }) => (
            <ItemRow
              key={rep.id}
              primary={rep.full_name}
              secondary={`${coverage}x coverage — quota $${Math.round(Number(rep.quota_annual) / 1000)}K`}
              badge={`${coverage}x`}
              onClick={() => setLocation(`/accounts?owner=${rep.id}`)}
            />
          ))
        )}
      </CoachingSection>
    </div>
  );
}
